import restClient from "api/restClient";
import { graphqlPost } from "api/graphqlClient";
import { Button } from "components/Button/Button";
import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordTable from "components/Table/RecordTable";
import { getInitialQueryWithTableSettings, TableColumn } from "components/Table/tableUtils";
import workspaceConfig from "config/resources/workspace";
import { getUserPermissionData } from "containers/Auth/Auth.selectors";
import { hasUserUpdateWorkspacePermission } from "containers/Auth/permissions";
import { logScreenId } from "containers/BundleManager/BundleManager";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { getLogListData } from "containers/Resources/Resources.selectors";
import { RecordLogLevel } from "graphql/generated/graphql";
import { History } from "history";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReconnectingWebSocket from "reconnecting-websocket";
import { getTypedLowerCase } from "utils/service";
import { useVisibilityTabChange } from "utils/useVisibilityTabChange";
import { getSearchField, parseFilterSafely } from "utils/utils";
import style from "./LogsListing.module.scss";
import { initialQuery } from "./constants";
import { clearLogsMutation, recordLogListSubscription } from "./fetchRecordLogList";

interface LogsListingProps {
    resourceName: string;
    id: string;
    additionalQueryField?: { [key: string]: any };
    queryInfo: {
        limit: number;
        offset: number;
        order: string;
    };
    columns: TableColumn[];
    updateQuery: (newQuery: any) => void;
    idList?: string[];
    history: History;
    queryParams: { [key: string]: string };
    isLiveUpdatesEnabled: boolean;
    tableSettingsKey?: string;
}

export const LogsListing = ({
    resourceName,
    id,
    additionalQueryField,
    queryInfo,
    columns,
    updateQuery,
    idList,
    history,
    queryParams,
    isLiveUpdatesEnabled,
    tableSettingsKey,
}: LogsListingProps) => {
    const dispatch = useDispatch();

    const permissionData = useSelector(getUserPermissionData);
    const canUserUpdateWorkspace = useMemo(
        () => hasUserUpdateWorkspacePermission(permissionData),
        [permissionData]
    );

    const recordLogDataSelector = (state: any) => getLogListData(state, resourceName, id);
    const recordLogData = useSelector(recordLogDataSelector);
    const recordLogList = recordLogData?.record_log_list?.record_log_list;
    const recordLogCount = recordLogData?.record_log_list?.record_log_count || 0;

    const [isLoading, setLoading] = useState(false);

    const isWorkspace = resourceName === workspaceConfig.resourceName;

    const { closeAllWebsocketConnections } = useVisibilityTabChange();

    const buildFilterStr = useCallback(
        (includeAdditional: boolean) => {
            const filters: Array<any> = [];
            if (queryParams.filter) {
                const filterOptions = parseFilterSafely(queryParams.filter);
                const filtersMappedOptions = (filterOptions.and || []).map((field) => {
                    if (field.field === "recordLogType") {
                        return {
                            field: "record_id--record.id:record_type",
                            op: "in",
                            value: field.value.map((value) =>
                                value.toUpperCase().replaceAll(" ", "_")
                            ),
                        };
                    } else if (field.field === "recordLogName") {
                        return getSearchField({
                            term: field.value,
                            field: "record_id--record.id:name",
                            idFieldName: "record_id--record.id:id",
                        });
                    } else if (field.field === "subjectOrData") {
                        const subjectField = getSearchField({
                            term: field.value,
                            field: "subject",
                        });
                        const dataField = getSearchField({ term: field.value, field: "data" });
                        return { or: [subjectField, dataField] };
                    }
                    return field;
                })
                    // Drop `in` clauses whose value list is empty. An empty
                    // multiselect (e.g. no record types selected) means "no
                    // constraint", NOT "match nothing" — forwarding it as
                    // `record_type IN ()` matches zero rows and renders a
                    // permanently-empty Logs tab. This surfaced as the Logs tab
                    // showing "no matching data" whenever a stale
                    // `recordLogType in []` filter was persisted for a record
                    // (operator-task-fe-operator-identity-execution-log-read-gap).
                    .filter(
                        (clause) =>
                            !(
                                clause &&
                                clause.op === "in" &&
                                Array.isArray(clause.value) &&
                                clause.value.length === 0
                            )
                    );
                filters.push(...filtersMappedOptions);
            }
            if (includeAdditional && additionalQueryField && !isEmpty(additionalQueryField)) {
                filters.push(...Object.values(additionalQueryField));
            }
            return filters.length > 0 ? JSON.stringify({ and: filters }) : null;
        },
        [queryParams.filter, additionalQueryField]
    );

    useEffect(() => {
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        let socket: ReconnectingWebSocket | null = null;

        const performSearch = () => {
            setLoading(true);
            const filterStr = buildFilterStr(true);

            const websocketParams = {
                subscription: recordLogListSubscription,
                variables: {
                    order: queryInfo.order,
                    limit: queryInfo.limit,
                    offset: queryInfo.offset,
                    filter: filterStr,
                },
            };
            if (socket) {
                socket.close();
            }
            // Send the subscription in the first websocket message instead of the
            // URL query string: GraphQL must never travel in a URL (it leaks into
            // history/proxy/access logs and enables CSRF on mutations).
            socket = new ReconnectingWebSocket(
                `${websocketHost}${
                    typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE
                }/graphql?subscription_message=true`
            );
            socket?.addEventListener("open", () => {
                socket?.send(JSON.stringify(websocketParams));
            });
            socket?.addEventListener("message", (event) => {
                const response = JSON.parse(event.data);

                if (response.BadRequest) {
                    const { title, message } = response.BadRequest;
                    NotificationService.error(
                        <ExpandableNotification title={title} details={message} />
                    );

                    if (socket) {
                        socket.close();
                    }
                    unlisten();
                } else {
                    dispatch({
                        type: resourcesActions.STORE_RESOURCE_DATA,
                        iri: `/${resourceName}/${id}/log-list`,
                        response,
                    });
                }

                setLoading(false);
            });
        };
        const unlisten = history.listen(performSearch);

        if (!closeAllWebsocketConnections && isLiveUpdatesEnabled) {
            performSearch();
        }

        return () => {
            socket?.close();
            unlisten();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        history,
        id,
        isWorkspace,
        queryInfo.limit,
        queryInfo.offset,
        queryInfo.order,
        resourceName,
        additionalQueryField,
        closeAllWebsocketConnections,
        queryParams.filter,
        isLiveUpdatesEnabled,
    ]);

    const changeQuery = useCallback((newQuery) => updateQuery(newQuery), [updateQuery]);

    const [isClearingLogs, setIsClearingLogs] = useState(false);
    const [isDownloadingLogs, setIsDownloadingLogs] = useState(false);

    const downloadLogs = async () => {
        setIsDownloadingLogs(true);
        try {
            const params: Record<string, string> = { order: "+created_at" };
            if (!isWorkspace) {
                params.record_id = id;
            }
            const filterStr = buildFilterStr(isWorkspace);
            if (filterStr) {
                params.filter = filterStr;
            }
            const response = await restClient.get("/record_log/export", {
                params,
                responseType: "blob",
            });
            const contentDisposition = response.headers["content-disposition"] || "";
            const match = contentDisposition.match(/filename="([^"]+)"/);
            const fileName = match ? match[1] : "logs.log";
            const url = URL.createObjectURL(new Blob([response.data], { type: "text/plain" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error: any) {
            NotificationService.error(<RestErrorNotification error={error} />);
        } finally {
            setIsDownloadingLogs(false);
        }
    };

    const clearLogs = () => {
        setIsClearingLogs(true);

        if (isWorkspace) {
            restClient
                .delete(`/record_log`)
                .catch((error) =>
                    NotificationService.error(<RestErrorNotification error={error} />)
                )
                .finally(() => setIsClearingLogs(false));
        } else {
            const promises = (idList ?? [id])?.map((id) =>
                graphqlPost({
                    query: clearLogsMutation(
                        resourceName === "bundles" ? "bundle_repository" : resourceName
                    ),
                    variables: { id },
                })
                    .catch((error) =>
                        NotificationService.error(<RestErrorNotification error={error} />)
                    )
            );
            Promise.allSettled(promises).finally(() => setIsClearingLogs(false));
        }
    };
    const button = (callback) => (
        <Button
            onClick={callback}
            label="Delete all logs"
            loading={isClearingLogs}
            variation="dark"
            disabled={isLoading || recordLogCount === 0 || !isLiveUpdatesEnabled}
        />
    );

    const showWarning = () =>
        showConfirmModal({
            content: (
                <div className="warningContent">
                    This will remove <span style={{ fontWeight: 700 }}>all</span> logs. Are you
                    sure?
                </div>
            ),
            onConfirm: clearLogs,
            onCancel: () => {},
            cancelButtonProps: cancelButtonProps,
            confirmButtonProps: { className: "button button--primary-blue" },
            className: "confirmationModal",
            centered: true,
        });

    const clearLogsButton = !isWorkspace
        ? button(showWarning)
        : canUserUpdateWorkspace && button(showWarning);

    const downloadLogsButton = (
        <Button
            onClick={downloadLogs}
            label="Download logs"
            loading={isDownloadingLogs}
            variation="dark"
            disabled={recordLogCount === 0 || isDownloadingLogs}
        />
    );

    return (
        <div id={logScreenId}>
            <div className={style.clearLogsButton}>
                {downloadLogsButton}
                {clearLogsButton}
            </div>
            <RecordTable
                data={recordLogList}
                totalItemCount={recordLogCount}
                columns={columns}
                isLoading={isLoading}
                recordTypes={null}
                updateQuery={changeQuery}
                options={{
                    density: "medium",
                    selectableRows: "none",
                    // "controls" = right-side column selector without the left
                    // bulk-action chrome (this panel is selectableRows:"none").
                    toolbar: "controls",
                    showDensityToggle: false,
                    toolbarFilters: false,
                    showSearch: false,
                    setRowProps: (row, dataIndex, rowIndex) => {
                        const currenRowData = recordLogList[dataIndex];
                        const className = getTypedLowerCase(
                            currenRowData?.level?.toLowerCase() as RecordLogLevel
                        );
                        return { className };
                    },
                    viewColumns: true,
                }}
                initialQuery={
                    tableSettingsKey
                        ? getInitialQueryWithTableSettings(tableSettingsKey, initialQuery)
                        : initialQuery
                }
                tableSettingsKey={tableSettingsKey}
                className={style.logsListingTable}
                isDisabled={!isLiveUpdatesEnabled}
            />
        </div>
    );
};
